import { ExtendedImmunization } from '@seen/core/models/immunizations/ExtendedImmunization'
import { ExtendedImmunizationRecommendation } from '@seen/core/models/immunizations/ExtendedImmunizationRecommendation'
import { Button, ButtonGroup, TypographyPageTitle } from '@seen/ui'
import { IconList, IconPlus, IconTable } from '@tabler/icons-react'
import { type JSX, useCallback, useState } from 'react'
import { useNavigate } from 'react-router'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { CairSyncStatus } from '#src/components/patient/immunization/CairSyncStatus.tsx'
import { ImmunizationDoseView } from '#src/components/patient/immunization/ImmunizationDoseView.tsx'
import { ImmunizationForm } from '#src/components/patient/immunization/ImmunizationForm.tsx'
import { ImmunizationListView } from '#src/components/patient/immunization/ImmunizationListView.tsx'
import { useCurrentParticipant } from '#src/contexts/current-participant/useCurrentParticipant.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

type ViewMode = 'list' | 'dose'

export function PatientImmunizationsPage(): JSX.Element {
  const participant = useCurrentParticipant()
  const isReadOnly = useIsReadOnly()
  const navigate = useNavigate()
  const { data: immunizationsList } = useSearch(ExtendedImmunization, {
    patient: participant.reference,
    'status:not': 'entered-in-error',
    _sort: '-date',
  })

  // Fetch immunization recommendations
  const { data: immunizationRecommendations } = useSearch(
    ExtendedImmunizationRecommendation,
    {
      patient: participant.reference,
    }
  )

  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [isAddImmunizationDialogOpen, setIsAddImmunizationDialogOpen] =
    useState(false)
  const [prefilledVaccineCode, setPrefilledVaccineCode] = useState<{
    code: string
    display: string
  } | null>(null)

  const handleOpenForm = useCallback(
    (vaccineCode?: { code: string; display: string }) => {
      setPrefilledVaccineCode(vaccineCode ?? null)
      setIsAddImmunizationDialogOpen(true)
    },
    []
  )

  return (
    <>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TypographyPageTitle>Immunizations</TypographyPageTitle>
            <CairSyncStatus participant={participant} />
          </div>
          <div className="flex items-center gap-2">
            <ButtonGroup>
              <Button
                variant={viewMode === 'list' ? 'default' : 'outline'}
                onClick={() => setViewMode('list')}
                className="gap-2"
              >
                <IconList className="size-4" />
              </Button>
              <Button
                variant={viewMode === 'dose' ? 'default' : 'outline'}
                onClick={() => setViewMode('dose')}
                className="gap-2"
              >
                <IconTable className="size-4" />
              </Button>
            </ButtonGroup>
            {!isReadOnly && (
              <Button
                onClick={() => handleOpenForm()}
                className="gap-2"
                variant="outline"
              >
                <IconPlus className="size-4" />
                New Immunization
              </Button>
            )}
          </div>
        </div>

        {viewMode === 'list' ? (
          <ImmunizationListView
            immunizations={immunizationsList ?? []}
            onRowClick={(immunization) => {
              void navigate(
                `/Patient/${participant.id}/immunizations/${immunization.id}`
              )
            }}
          />
        ) : (
          <ImmunizationDoseView
            immunizations={immunizationsList ?? []}
            immunizationRecommendations={immunizationRecommendations}
            participantId={participant.id}
            onNavigate={(immunizationId) => {
              void navigate(
                `/Patient/${participant.id}/immunizations/${immunizationId}`
              )
            }}
            onAddClick={handleOpenForm}
          />
        )}
      </div>

      <Dialog
        open={isAddImmunizationDialogOpen}
        onOpenChange={(open) => {
          setIsAddImmunizationDialogOpen(open)
          if (!open) {
            setPrefilledVaccineCode(null)
          }
        }}
      >
        <DialogDisplay>
          <DialogHeader>
            <DialogTitle>Add Immunization</DialogTitle>
          </DialogHeader>
          <DialogContent>
            <ImmunizationForm
              onSubmit={() => {
                setIsAddImmunizationDialogOpen(false)
                setPrefilledVaccineCode(null)
              }}
              onCancel={() => {
                setIsAddImmunizationDialogOpen(false)
                setPrefilledVaccineCode(null)
              }}
              defaultValues={
                prefilledVaccineCode
                  ? {
                      vaccine: {
                        code: prefilledVaccineCode.code,
                        display: prefilledVaccineCode.display,
                      },
                    }
                  : undefined
              }
            />
          </DialogContent>
        </DialogDisplay>
      </Dialog>
    </>
  )
}
